import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { Textarea } from '@/design-system/textarea';
import {
  reasonsAPI,
  CategorizedReasons,
  ReasonOption,
} from '@/services/api/reasons';

interface CancelDeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (notes: string, reasonCode: string) => void;
  isLoading?: boolean;
  deliveryStatus?: string;
}

const labelCls = 'mb-1 block text-[11px] font-medium text-text-sub-600';

const CancelDeliveryModal: React.FC<CancelDeliveryModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  deliveryStatus,
}) => {
  const [reasons, setReasons] = useState<CategorizedReasons>({});
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [selectedReason, setSelectedReason] = useState<ReasonOption | null>(
    null
  );
  const [customNotes, setCustomNotes] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSelectedReason(null);
      setCustomNotes('');
      return;
    }
    setLoadingReasons(true);
    reasonsAPI
      .getCategorized({
        type: 'CANCELLATION',
        language: 'ID',
        ...(deliveryStatus ? { status: deliveryStatus } : {}),
        excludeActors: 'CLIENT,SENDER,RECIPIENT',
      })
      .then((data) => setReasons(data))
      .catch(() => setReasons({}))
      .finally(() => setLoadingReasons(false));
  }, [isOpen, deliveryStatus]);

  const isOther =
    selectedReason?.internalReason?.toLowerCase().includes('lainnya') ||
    selectedReason?.internalReason?.toLowerCase().includes('others') ||
    selectedReason?.internalReason?.toLowerCase().includes('other');

  const canSubmit =
    selectedReason !== null &&
    (!isOther || customNotes.trim().length >= 3);

  const handleSubmit = () => {
    if (!selectedReason) return;
    const notes = isOther ? customNotes.trim() : selectedReason.internalReason;
    onConfirm(notes, selectedReason.code);
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-close-circle-line" />}
      iconTone="danger"
      title="Confirm Cancel Delivery?"
      subtitle="Jelaskan mengapa delivery dibatalkan."
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            Batal
          </Button>
          <Button
            tone="destructive"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
            loading={isLoading}
          >
            {isLoading ? 'Memproses…' : 'Cancel Delivery'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Divider label */}
        <div className="flex items-center gap-2">
          <div className="flex-1 border-t border-stroke-soft-200" />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-soft-400">
            Reason for Cancellation
          </span>
          <div className="flex-1 border-t border-stroke-soft-200" />
        </div>

        {loadingReasons ? (
          <div className="flex items-center justify-center py-8 text-text-soft-400">
            <i className="ri-loader-4-line animate-spin text-lg mr-2" />
            <span className="text-sm">Loading reasons…</span>
          </div>
        ) : Object.keys(reasons).length === 0 ? (
          <p className="text-sm text-text-soft-400 py-4 text-center">
            No reasons available
          </p>
        ) : (
          <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
            {Object.entries(reasons).map(([category, options]) => (
              <div key={category}>
                <p className={labelCls}>{category}</p>
                <div className="space-y-2 ml-1">
                  {options.map((opt) => (
                    <label
                      key={opt.id}
                      className="flex items-start gap-2.5 cursor-pointer group"
                    >
                      <input
                        type="radio"
                        name="cancel-reason"
                        value={String(opt.id)}
                        checked={selectedReason?.id === opt.id}
                        onChange={() => {
                          setSelectedReason(opt);
                          setCustomNotes('');
                        }}
                        className="mt-0.5 accent-accent shrink-0"
                      />
                      <span className="text-sm text-text-sub-600 group-hover:text-text-strong-950 leading-snug">
                        {opt.internalReason}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Custom notes textarea for "Other" */}
        {isOther && (
          <div>
            <p className={labelCls}>Keterangan tambahan</p>
            <Textarea
              value={customNotes}
              onChange={(e) => setCustomNotes(e.target.value)}
              maxLength={200}
              rows={3}
              placeholder="Jelaskan alasan pembatalan…"
              className="resize-none"
            />
            <p className="mt-1 text-right text-[11px] text-text-soft-400">
              {customNotes.length}/200
            </p>
          </div>
        )}
      </div>
    </FormModal>
  );
};

export default CancelDeliveryModal;
